/**
 * The front door.
 *
 * THE ONE PROPERTY THIS SCREEN MUST NOT BREAK: the answer is identical whether or not the address
 * belongs to a client. The server already guarantees it — every branch of `POST /api/auth/sign-in`
 * returns the same body, including the malformed-address branch — and a UI that renders anything
 * conditional on the reply would hand that guarantee straight back. So there is exactly one success
 * state here, it says "if that address is on file", and it is reached by every submission that did
 * not fail at the transport. Do not add "we don't recognise that address", do not add a
 * "resend" that behaves differently the second time, and do not vary the timing.
 *
 * There is no password field and never will be. The link in the email is the credential.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react'

import { signIn } from '../api/client'
import { PHONE_DISPLAY, PHONE_HREF } from '../model/contact'
import { BrandPaw, Icon, UI_ICON_PATHS } from '../model/icons'

export interface SignInProps {
  /**
   * A sentence to show above the form — in practice the reason a magic link was rejected
   * ("That link has expired…"). Required and nullable rather than optional: under
   * `exactOptionalPropertyTypes` an optional prop cannot be handed an explicit `undefined`, and
   * the caller genuinely has a null case.
   */
  readonly notice: string | null
  /**
   * Show the privacy notice.
   *
   * Reachable from here rather than only from inside the app because UK GDPR Article 13 is about
   * what someone is told BEFORE they hand over their details — a notice that only appears once you
   * have signed in has been shown to the wrong person at the wrong time.
   */
  readonly onShowPrivacy: () => void
}

type Phase =
  | { readonly state: 'idle' }
  | { readonly state: 'sending' }
  /** `message` is the server's own generic sentence. `devLink` only exists in bootstrap mode. */
  | { readonly state: 'sent'; readonly message: string; readonly devLink: string | null }
  | { readonly state: 'failed'; readonly message: string }

/**
 * A deliberately permissive check — one `@`, something either side of it, no whitespace.
 *
 * Every "proper" email regex rejects addresses that are valid and in use, and the cost of a false
 * rejection here is a real client who cannot get in and has no way to argue with a text field.
 * The server is the authority on whether an address matches a household; this only catches the
 * genuine slip (an empty box, a name, a phone number) before spending a request on it.
 */
function looksLikeAnAddress(value: string): boolean {
  const at = value.indexOf('@')
  return at > 0 && at === value.lastIndexOf('@') && at < value.length - 1 && !/\s/.test(value)
}

export function SignIn({ notice, onShowPrivacy }: SignInProps): ReactElement {
  const [email, setEmail] = useState('')
  const [invalid, setInvalid] = useState(false)
  const [phase, setPhase] = useState<Phase>({ state: 'idle' })

  const sending = phase.state === 'sending'

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (sending) return

    const address = email.trim()
    if (!looksLikeAnAddress(address)) {
      setInvalid(true)
      return
    }

    setInvalid(false)
    setPhase({ state: 'sending' })

    const result = await signIn(address)
    if (result.ok) {
      setPhase({ state: 'sent', message: result.data.message, devLink: result.data.devLink ?? null })
      return
    }
    // Only a transport or rate-limit failure reaches here. A 200 with an unmatched address is a
    // success as far as this screen is concerned, and must stay one.
    setPhase({ state: 'failed', message: result.error.message })
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
      }}
    >
      <div style={{ width: 'min(430px, 100%)', animation: 'ft-rise .4s ease both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 4px 22px' }}>
          <span
            aria-hidden="true"
            style={{
              width: '46px',
              height: '46px',
              flex: 'none',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            <BrandPaw />
          </span>
          <span>
            <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '19px', lineHeight: 1.1 }}>
              Fairy Tails
            </span>
            <span className="text-muted" style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
              My Dog · client portal
            </span>
          </span>
        </div>

        {phase.state === 'sent' ? (
          <SentCard
            message={phase.message}
            devLink={phase.devLink}
            onStartAgain={() => {
              setPhase({ state: 'idle' })
              setInvalid(false)
            }}
          />
        ) : (
          <section className="card elev-sm" style={{ gap: 0, padding: '26px 24px' }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '.09em',
                textTransform: 'uppercase',
                color: 'var(--color-accent-700)',
                marginBottom: '7px',
              }}
            >
              Sign in
            </div>
            <h1 style={{ margin: '0 0 7px', fontSize: 'clamp(26px, 5vw, 32px)' }}>See your dog’s record</h1>
            <p className="text-muted" style={{ margin: '0 0 20px', fontSize: '14.5px' }}>
              Give us the email address we hold for you and we’ll send a link that signs you in.
              There is no password to remember.
            </p>

            {notice === null ? null : (
              <p
                role="alert"
                style={{
                  margin: '0 0 18px',
                  padding: '12px 16px',
                  borderRadius: '22px',
                  background: 'var(--color-accent-100)',
                  color: 'var(--color-accent-800)',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                {notice}
              </p>
            )}

            {/*
              `noValidate` so the browser's own bubble does not fire. The check is ours, the
              message is ours, and it is wired to the input with aria-describedby so it is
              announced rather than merely coloured.
            */}
            <form noValidate onSubmit={(event) => void onSubmit(event)}>
              <div className="field">
                <label htmlFor="ft-signin-email">Email address</label>
                <input
                  className="input"
                  id="ft-signin-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  disabled={sending}
                  aria-invalid={invalid}
                  aria-describedby={invalid ? 'ft-signin-error' : undefined}
                  onChange={(event) => {
                    setEmail(event.target.value)
                    if (invalid) setInvalid(false)
                  }}
                  style={{ minHeight: '44px' }}
                />
              </div>

              {invalid ? (
                <p
                  id="ft-signin-error"
                  role="alert"
                  style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--color-accent-800)' }}
                >
                  That does not look like an email address. It should look like name@example.co.uk.
                </p>
              ) : null}

              {phase.state === 'failed' ? (
                <p role="alert" style={{ margin: '14px 0 0', fontSize: '13px', lineHeight: 1.5 }}>
                  {phase.message}
                </p>
              ) : null}

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={sending}
                aria-busy={sending}
                style={{ minHeight: '44px', marginTop: '18px' }}
              >
                {sending ? 'Sending…' : 'Email me a link'}
                {sending ? null : <Icon paths={UI_ICON_PATHS.arrowRight} size={15} />}
              </button>
            </form>

            <p className="text-muted" style={{ margin: '16px 0 0', fontSize: '12.5px', lineHeight: 1.55 }}>
              The link works once. If you have changed your email address, or you are not sure which
              one we hold, ring us on{' '}
              <a href={PHONE_HREF} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {PHONE_DISPLAY}
              </a>{' '}
              and we will sort it out.
            </p>
          </section>
        )}

        {/*
          Outside the phase switch on purpose, so it is present on the form AND on the confirmation
          card. Article 13 information has to be available at the moment someone decides whether to
          type their address in, which is this screen in both of its states.
        */}
        <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: '12.5px' }}>
          <button
            type="button"
            onClick={onShowPrivacy}
            style={{
              padding: '8px',
              border: 0,
              background: 'none',
              font: 'inherit',
              fontWeight: 700,
              color: 'var(--color-accent-800)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            How we look after your information
          </button>
        </p>
      </div>
    </main>
  )
}

interface SentCardProps {
  readonly message: string
  readonly devLink: string | null
  readonly onStartAgain: () => void
}

/**
 * The confirmation. Note what it does NOT say: it never repeats the address back, and it never
 * claims an email was sent — only that one was sent *if* the address is on file. Echoing the
 * address would turn a shoulder-surfed screen into a "yes, that person is a client here".
 */
function SentCard({ message, devLink, onStartAgain }: SentCardProps): ReactElement {
  const heading = useRef<HTMLHeadingElement>(null)

  /*
    Move focus to the new heading rather than wrapping it in `role="status"`.

    Two reasons. A live region that is MOUNTED with its text already in it is not reliably
    announced — assistive tech has to be observing the region before the change, which is the same
    reason `<Toast>` is never unmounted. And `role="status"` on an `<h1>` REPLACES the heading role
    outright, so the page would silently stop having a level-1 heading. Focusing it announces the
    text and the heading level, and puts a keyboard user at the top of the new content instead of
    back at the start of the document.
  */
  useEffect(() => {
    heading.current?.focus()
  }, [])

  return (
    <section
      className="card elev-sm"
      style={{ alignItems: 'center', textAlign: 'center', gap: 0, padding: '36px 26px' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-accent-2-200)',
          color: 'var(--color-accent-2-800)',
          marginBottom: '14px',
        }}
      >
        <Icon paths={UI_ICON_PATHS.check} size={28} />
      </span>
      <h1 ref={heading} tabIndex={-1} style={{ margin: '0 0 6px', fontSize: '26px' }}>
        Check your email
      </h1>
      <p className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 6px', fontSize: '14px', lineHeight: 1.55 }}>
        {message}
      </p>
      <p className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 20px', fontSize: '13px', lineHeight: 1.55 }}>
        It can take a minute to arrive. If it does not, have a look in your junk or spam folder.
      </p>

      {devLink === null ? null : <DevLink href={devLink} />}

      <button type="button" className="btn btn-ghost" onClick={onStartAgain} style={{ minHeight: '44px' }}>
        Use a different address
      </button>
    </section>
  )
}

/**
 * The bootstrap escape hatch, shown loudly.
 *
 * The server only returns `devLink` when no mail transport is configured — the first-pilot-user
 * case, before Resend is wired up. It is a live, single-use credential rendered on screen, so it
 * is labelled as unmistakably not-normal rather than styled to blend in. If this ever appears in
 * front of a real client, the deployment has no mailer and that is the bug to fix.
 */
function DevLink({ href }: { readonly href: string }): ReactElement {
  return (
    <div
      style={{
        width: '100%',
        margin: '0 0 18px',
        padding: '16px',
        borderRadius: '22px',
        background: 'var(--color-neutral-100)',
        border: '1px solid var(--color-divider)',
        textAlign: 'left',
      }}
    >
      <span className="tag tag-accent" style={{ fontWeight: 700 }}>
        Development only
      </span>
      <p className="text-muted" style={{ margin: '10px 0 8px', fontSize: '12.5px', lineHeight: 1.55 }}>
        No mailer is configured on this server, so the sign-in link is shown here instead of being
        emailed. It works once.
      </p>
      <a href={href} style={{ fontSize: '13px', fontWeight: 700, overflowWrap: 'anywhere' }}>
        Open the sign-in link
      </a>
    </div>
  )
}
